/**
 * Run detail page: drill panel state, live-poller view sync, D3 site graph
 * mount (`site_graph` lazy-loads `/static/js/dist/site-graph.js`), lineage
 * popover, deep-eval form and timer.
 *
 * Markup contract: `run_detail` root carries data-run-id and the page has
 * #drill-target-side / #drill-target-inline and #live-poller; `site_graph`
 * root carries data-run-id or data-graph-url, optional data-clustered, and
 * x-ref="graphContainer"; `deep_eval_timer` root carries data-started-at.
 * `run_section_nav` root is `#run-section-nav`; its pills are `a[data-section]`
 * with an optional `[data-pill-badge]`.
 */
import { listen, set_poller_param, unlisten_all } from "./helpers.js";

// One download per page regardless of how many graphs mount or remount.
// Kept alongside the bundle cache rather than on Alpine's reactive
// per-instance state: it's the module's constructor, not per-instance data.
let siteGraphBundle = null;
let _SiteGraphRenderer = null;
function loadSiteGraphBundle() {
  if (!siteGraphBundle) {
    // A transient fetch failure must not kill every later mount: clear the
    // cache so the next mount retries the import instead of replaying it.
    siteGraphBundle = import("/static/js/dist/site-graph.js").catch((e) => {
      siteGraphBundle = null;
      throw e;
    });
  }
  return siteGraphBundle;
}

document.addEventListener("alpine:init", () => {
  // Run detail: keep #live-poller on the view the content zone is showing.
  // When a section card is expanded the poller must stop repainting the
  // overview grid.
  document.addEventListener("htmx:afterSwap", (evt) => {
    if (evt.detail.target.id !== "content-zone") return;
    const poller = document.getElementById("live-poller");
    if (!poller) return;
    const swapped = evt.detail.target.firstElementChild;
    const view = swapped ? swapped.getAttribute("data-view") || "overview" : "overview";
    set_poller_param(poller, "view", view);
  });

  // Toggleable popover that lazy-loads its content via HTMX the first time
  // it opens. The button toggles `open`; on first open we dispatch
  // `lineage:show-once` so the sibling panel's `hx-trigger="lineage:show-once"`
  // fires its GET and caches the rendered partial in the DOM. Used by the
  // run-header "View lineage" affordance — kept generic in case other surfaces
  // need the same pattern (HTMX-lazy popover).
  Alpine.data("lineage_popover", () => ({
    open: false,
    _loaded: false,
    toggle() {
      this.open = !this.open;
      if (this.open && !this._loaded) {
        this._loaded = true;
        // Use the next tick so x-show has applied before HTMX walks the DOM.
        this.$nextTick(() => {
          this.$dispatch("lineage:show-once");
        });
      }
    },
  }));

  // Run detail page-level component. Owns the drill-panel state for the
  // archetype-keyed Pages table: which row is open, which layout (side rail
  // ≥ 1100px, inline expand below) is active, and focus restoration after
  // the HTMX swap.
  Alpine.data("run_detail", () => ({
    _listeners: [],
    openId: null,
    drillLayout: "side",
    _runId: null,
    _savedFocus: null,
    _mq: null,
    init() {
      // Cache run id from the run_detail wrapper. Methods triggered from
      // descendant directives have ``$el`` bound to the descendant, so
      // ``this.$el.dataset.runId`` is undefined inside ``openDrill``.
      this._runId = this.$el.dataset.runId;
      this._mq = window.matchMedia("(min-width: 1100px)");
      this.drillLayout = this._mq.matches ? "side" : "inline";
      listen(this, this._mq, "change", () => {
        const next = this._mq.matches ? "side" : "inline";
        if (next === this.drillLayout) return;
        // Layout flipped — close any open drill so stale content in the
        // newly-inactive target doesn't linger when the user re-opens.
        if (this.openId !== null) this.closeDrill();
        this.drillLayout = next;
      });

      listen(this, window, "keydown", (evt) => {
        if (evt.key === "Escape" && this.openId !== null) this.closeDrill();
      });

      // Focus the drill close button after the HTMX swap completes so
      // keyboard users land inside the panel they just opened.
      listen(this, document, "htmx:afterSwap", (evt) => {
        const t = evt.detail?.target;
        if (!t) return;
        if (t.id !== "drill-target-side" && t.id !== "drill-target-inline") return;
        this.$nextTick(() => {
          const btn = t.querySelector(
            '[x-ref="drillClose"], [aria-label="Close archetype detail"]',
          );
          if (btn && typeof btn.focus === "function") btn.focus();
        });
      });

      // If the drill fetch errors (network failure, 4xx/5xx), close the
      // panel rather than leaving the user with an empty target and a
      // highlighted row that no longer reflects state.
      const onError = (evt) => {
        const t = evt.detail?.target;
        if (!t) return;
        if (t.id !== "drill-target-side" && t.id !== "drill-target-inline") return;
        if (this.openId !== null) this.closeDrill();
      };
      listen(this, document, "htmx:responseError", onError);
      listen(this, document, "htmx:sendError", onError);
    },
    openDrill(id) {
      // Skip if the user is mid-selection inside the row — a drag-select
      // shouldn't be hijacked into a drill open.
      const sel = window.getSelection?.();
      if (sel && String(sel).length > 0) return;
      // Re-clicking the open row toggles the panel closed.
      if (id === this.openId) {
        this.closeDrill();
        return;
      }
      if (!this._runId) return;
      this._savedFocus = document.activeElement;
      this.openId = id;
      const targetId = this.drillLayout === "side" ? "drill-target-side" : "drill-target-inline";
      const url =
        "/runs/" +
        encodeURIComponent(this._runId) +
        "/pages/" +
        encodeURIComponent(id) +
        "/drill?layout=" +
        encodeURIComponent(this.drillLayout);
      htmx.ajax("GET", url, { target: `#${targetId}`, swap: "innerHTML", source: this.$el });
    },
    closeDrill() {
      this.openId = null;
      const ids = ["drill-target-side", "drill-target-inline"];
      for (let i = 0; i < ids.length; i++) {
        const el = document.getElementById(ids[i]);
        if (el && typeof el.replaceChildren === "function") el.replaceChildren();
      }
      const prev = this._savedFocus;
      this._savedFocus = null;
      if (prev && document.contains(prev) && typeof prev.focus === "function") {
        this.$nextTick(() => {
          prev.focus();
        });
      }
    },
    destroy() {
      unlisten_all(this);
    },
  }));

  Alpine.data("site_graph", () => ({
    _listeners: [],
    loading: true,
    error: null,
    showExternal: false,
    model: null,
    renderer: null,
    personaId: null,
    runId: null,
    graphUrl: null,
    clustered: null,

    init() {
      this.runId = this.$el.dataset.runId;
      // Data source: an explicit data-graph-url (project-level cumulative graph)
      // takes precedence; otherwise build the run-level endpoint from
      // data-run-id. This is the only difference between the two graph views —
      // the model/renderer are data-source agnostic.
      this.graphUrl = this.$el.dataset.graphUrl || `/runs/${this.runId}/graph.json`;
      // When data-clustered is "true" or "false", pass it explicitly to the
      // graph endpoint. When the attribute is absent the request omits the
      // param so the server default (clustered=True) applies.
      const clusteredAttr = this.$el.dataset.clustered;
      if (clusteredAttr === "true") this.clustered = true;
      else if (clusteredAttr === "false") this.clustered = false;
      loadSiteGraphBundle()
        .then((mod) => this._setup(mod))
        .catch((err) => {
          console.error("Site graph bundle failed to load:", err);
          this.loading = false;
          this.error = "Failed to load graph dependencies.";
        });
    },

    _setup(mod) {
      _SiteGraphRenderer = mod.SiteGraphRenderer;
      this.model = new mod.SiteGraphModel(this.graphUrl);
      this._mq = window.matchMedia("(min-width: 1024px)");
      listen(this, this._mq, "change", () => {
        this.$nextTick(() => window.dispatchEvent(new Event("resize")));
      });
      this.loadGraph();
    },

    filterPersona(personaId) {
      this.personaId = personaId || null;
      this.loadGraph();
      const tableView = document.getElementById("table-view");
      if (tableView) {
        const url =
          "/runs/" +
          encodeURIComponent(this.runId) +
          "/_pages" +
          (this.personaId ? `?persona_id=${encodeURIComponent(this.personaId)}` : "");
        htmx.ajax("GET", url, { target: "#table-view", swap: "innerHTML", source: this.$el });
      }
    },

    loadGraph() {
      this.loading = true;
      this.error = null;
      this.model
        .load(this.personaId, this.clustered)
        .then(() => {
          if (this.renderer) this.renderer.destroy();
          this.renderer = new _SiteGraphRenderer(this.$refs.graphContainer, this.model);
          this.renderer.render(
            this.model.getNodes(this.showExternal),
            this.model.getEdges(this.showExternal),
          );
        })
        .catch((err) => {
          console.error("Site graph load failed:", err);
          this.error = "Failed to load graph data.";
        })
        .finally(() => {
          this.loading = false;
        });
    },

    toggleExternal() {
      if (this.renderer && this.model) {
        this.renderer.render(
          this.model.getNodes(this.showExternal),
          this.model.getEdges(this.showExternal),
        );
      }
    },

    destroy() {
      if (this.renderer) this.renderer.destroy();
      unlisten_all(this);
    },
  }));

  Alpine.data("deep_eval_form", () => ({
    init() {
      this.$el.addEventListener("htmx:afterRequest", (event) => {
        if (event.detail?.successful) {
          this.$dispatch("deep-eval-submitted");
        }
      });
    },
  }));

  Alpine.data("deep_eval_timer", () => ({
    elapsed: 0,
    elapsedLabel: "0s",
    _startedAt: null,
    _timer: null,
    init() {
      // Capture data-started-at off the scope root before any descendant
      // directive fires (per the design-system rules in CLAUDE.md). Falls
      // back to "now" if the attribute is missing or unparseable.
      const raw = this.$el?.dataset ? this.$el.dataset.startedAt : "";
      const parsed = raw ? Date.parse(raw) : NaN;
      this._startedAt = Number.isNaN(parsed) ? Date.now() : parsed;
      this._tick();
      this._timer = setInterval(() => {
        this._tick();
      }, 1000);
    },
    _tick() {
      const seconds = Math.max(0, Math.floor((Date.now() - this._startedAt) / 1000));
      this.elapsed = seconds;
      if (seconds < 60) {
        this.elapsedLabel = `${seconds}s`;
      } else {
        const mins = Math.floor(seconds / 60);
        const rem = seconds % 60;
        this.elapsedLabel = `${mins}m ${rem}s`;
      }
    },
    destroy() {
      if (this._timer) clearInterval(this._timer);
    },
  }));

  // Section pills live outside #content-zone, so a pill click swaps the zone
  // and pushes a URL without re-rendering the nav. Reconcile the active pill
  // from the URL whenever htmx pushes history or the user goes back/forward.
  const ACTIVE_PILL = ["bg-accent-100", "text-accent-800"];
  const INACTIVE_PILL = ["bg-gray-100", "text-gray-600", "hover:bg-gray-200"];
  Alpine.data("run_section_nav", () => ({
    _listeners: [],
    _links: [],
    init() {
      this._links = Array.from(this.$el.querySelectorAll("a[data-section]"));
      listen(this, document.body, "htmx:pushedIntoHistory", () => this.sync());
      listen(this, window, "popstate", () => this.sync());
      this.sync();
    },
    sync() {
      const current = new URLSearchParams(window.location.search).get("section") || "";
      for (const link of this._links) {
        const active = link.dataset.section === current;
        link.classList.remove(...(active ? INACTIVE_PILL : ACTIVE_PILL));
        link.classList.add(...(active ? ACTIVE_PILL : INACTIVE_PILL));
        if (active) link.setAttribute("aria-current", "true");
        else link.removeAttribute("aria-current");
        const badge = link.querySelector("[data-pill-badge]");
        if (badge) {
          badge.classList.toggle("text-accent-500", active);
          badge.classList.toggle("text-gray-400", !active);
        }
      }
    },
    destroy() {
      unlisten_all(this);
    },
  }));
});
